// src/pages/PlatformInbox.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import "../styles/PlatformInbox.css";
import { useNavigate } from "react-router-dom";

/* ───────────────────────────────────────────────────────────── */
/* Status options */

const SALES_STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "in_conversation", label: "In conversation" },
  { value: "qualified", label: "Qualified" },
  { value: "closed", label: "Closed" },
];

const SUPPORT_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
];

const statusLabel = (raw) => {
  const s = String(raw || "")
    .toLowerCase()
    .trim();
  if (!s) return "-";
  return s.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
};

// Used for your badge colors
const statusKey = (raw) => {
  const s = String(raw || "")
    .toLowerCase()
    .trim();
  if (!s) return "new";

  // sales
  if (["new"].includes(s)) return "new";
  if (["contacted", "in_conversation", "qualified"].includes(s)) return "open";
  if (["closed"].includes(s)) return "closed";

  // support
  if (["open", "in_progress"].includes(s)) return "open";
  if (["resolved"].includes(s)) return "resolved";

  return "open";
};

/* ───────────────────────────────────────────────────────────── */

export default function PlatformInbox() {
  const navigate = useNavigate();

  const [tab, setTab] = useState("sales"); // "sales" | "support"
  const [salesRequests, setSalesRequests] = useState([]);
  const [contactRequests, setContactRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    const qSales = query(
      collection(db, "sales_request"),
      orderBy("createdAt", "desc")
    );

    const qSupport = query(
      collection(db, "contact_requests"),
      orderBy("createdAt", "desc")
    );

    const unsubSales = onSnapshot(qSales, (snap) => {
      setSalesRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    const unsubSupport = onSnapshot(qSupport, (snap) => {
      setContactRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubSales();
      unsubSupport();
    };
  }, []);

  const activeRows = useMemo(() => {
    const rows = tab === "sales" ? salesRequests : contactRequests;
    const s = search.trim().toLowerCase();
    if (!s) return rows;

    return rows.filter((r) => {
      const fullName = (r.fullName || "").toLowerCase();
      const email = (r.email || "").toLowerCase();
      const company = (r.company || "").toLowerCase();
      const message = (r.message || r.details || "").toLowerCase();
      return (
        fullName.includes(s) ||
        email.includes(s) ||
        company.includes(s) ||
        message.includes(s)
      );
    });
  }, [tab, salesRequests, contactRequests, search]);

  const formatCreated = (createdAt) => {
    try {
      if (createdAt?.toDate) return createdAt.toDate().toLocaleString();
      return "";
    } catch {
      return "";
    }
  };

  const updateRowStatus = async (rowId, nextStatus) => {
    const col = tab === "sales" ? "sales_request" : "contact_requests";
    setUpdatingId(rowId);

    try {
      await updateDoc(doc(db, col, rowId), {
        status: nextStatus,
      });
    } catch (e) {
      console.error("Failed to update status:", e);
      alert("Could not update status. Please try again.");
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="platformInbox-page">
      {/* Header row */}
      <div className="platformInbox-top">
        <div className="platformInbox-titleWrap">
          <button
            className="platformInbox-back"
            onClick={() => navigate("/admin-dashboard")}
            type="button"
          >
            ← Back to Dashboard
          </button>

          <h1>Platform Inbox</h1>
          <p className="platformInbox-subtitle">
            View sales requests and support/contact requests.
          </p>
        </div>

        <div className="platformInbox-searchWrap">
          <input
            className="platformInbox-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, company, message..."
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="platformInbox-filters">
        <div className="platformInbox-pills">
          <button
            className={`platformInbox-pill ${tab === "sales" ? "active" : ""}`}
            onClick={() => setTab("sales")}
            type="button"
          >
            Sales Requests{" "}
            <span className="count">({salesRequests.length})</span>
          </button>

          <button
            className={`platformInbox-pill ${
              tab === "support" ? "active" : ""
            }`}
            onClick={() => setTab("support")}
            type="button"
          >
            Support Requests{" "}
            <span className="count">({contactRequests.length})</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="platformInbox-card">
        <div className="platformInbox-tableWrap">
          <table className="platformInbox-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Message</th>
                <th>Status</th>
                <th>Source</th>
              </tr>
            </thead>

            <tbody>
              {activeRows.map((r) => {
                const rawStatus = String(r.status || "")
                  .toLowerCase()
                  .trim();

                const defaultStatus = tab === "sales" ? "new" : "open";
                const value = rawStatus || defaultStatus;

                const options =
                  tab === "sales"
                    ? SALES_STATUS_OPTIONS
                    : SUPPORT_STATUS_OPTIONS;

                return (
                  <tr key={r.id}>
                    <td className="platformInbox-muted">
                      {formatCreated(r.createdAt)}
                    </td>

                    <td>{r.fullName || "-"}</td>

                    <td className="platformInbox-email">{r.email || "-"}</td>

                    <td>{r.company || "-"}</td>

                    <td>
                      <div className="platformInbox-message">
                        {(r.message || r.details || "-").toString()}
                      </div>
                    </td>

                    <td>
                      <div className="platformInbox-statusCell">
                        <span
                          className={`platformInbox-status ${statusKey(
                            r.status
                          )}`}
                        >
                          {statusLabel(r.status)}
                        </span>

                        <select
                          className="platformInbox-statusSelect"
                          value={value}
                          onChange={(e) =>
                            updateRowStatus(r.id, e.target.value)
                          }
                          disabled={updatingId === r.id}
                        >
                          {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>

                    <td className="platformInbox-muted">
                      {r.source || r.page || "-"}
                    </td>
                  </tr>
                );
              })}

              {!activeRows.length && (
                <tr>
                  <td className="platformInbox-empty" colSpan={7}>
                    No requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
